import React, { useState } from "react";
import { Backdrop, Box, CircularProgress, Typography, makeStyles, TextField, Tooltip } from "@material-ui/core";
import PropTypes from "prop-types";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import { useTranslation } from "react-i18next";
import AppConfig from "config/AppConfig";
import { Formik, Form, Field } from "formik";
import AvatarUpload from "components/UploadFile/AvatarUpload";
import { useDispatch, useSelector } from "react-redux";
import { updateAvatar, fetchWorkerDetail, activeWorker, deactivateWorker, terminateWorker, reactivateWorker, resendOnboarding } from "store/slices/worker";
import AppConfirmDialog from "components/Dialog/AppConfirmDialog";
import AppDialog from "components/Dialog/AppDialog";
import AppSelect from "components/Select/AppSelect";
import { getAuthInfo } from "store/slices/auth";
import { SYSTEM_ROLE } from "config/AppConfig";
import moment from "moment";
import { FILE_STATUS } from "config/AppConfig";

const useStyles = makeStyles(() => ({
  container: {
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    padding: "16px 14px",
    textAlign: "center",
  },
  backdrop: {
    zIndex: 99999,
    color: '#fff',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  name: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 2,
  },
  role: {
    fontSize: 11,
    color: "#6B7280",
    marginBottom: 8,
  },
  statusPill: {
    background: "#2DD4BF",
    color: "#FFFFFF",
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    padding: "2px 12px",
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
  },
  onboardingSection: {
    marginTop: 20,
    textAlign: "left",
    borderTop: "1px solid #F3F4F6",
    paddingTop: 16,
  },
  onboardingTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: 12,
    textAlign: "center",
  },
  onboardingItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
    fontSize: 12,
  },
  itemLabel: {
    color: "#6B7280",
  },
  donePill: {
    color: "#0F766E",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 400,
    background: "#CCFBF1",
  },
  warningPill: {
    color: "#92400e",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 400,
    background: "#fef3c7",
  },
  dangerPill: {
    color: "#991B1B",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 400,
    background: "#FEE2E2",
  },
  avatarWrapper: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 10
  }
}));

const StaffOnboarding = ({ staffDetail, onResendOnboarding, onChange }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isUploading, setIsUploading] = useState(false);
  const [showConfirmActive, setShowConfirmActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmDeactivate, setShowConfirmDeactivate] = useState(false);
  const [showConfirmTerminate, setShowConfirmTerminate] = useState(false);
  const [showConfirmReactivate, setShowConfirmReactivate] = useState(false);
  // const [showConfirmReject, setShowConfirmReject] = useState(false);
  const [reason, setReason] = useState('');
  const [terminationReason, setTerminationReason] = useState('');
  const authInfo = useSelector(getAuthInfo) || {};

  const onboardingItems = [
    { key: 'workerNdisWorkerScreeningCheckResponseList', label: t('document.ndisWorkerScreeningCheck') },
    { key: "workerNdisOrientationModuleResponseList", label: t("trainingTag.workerNdisOrientationModule") },
    { key: 'workerDriverLicenceResponseList', label: t('document.driverLicence100Point') },
    { key: 'workerCertificationResponseList', label: t('document.passport100Point') },
    { key: 'workerRightToWorkInAustraliaResponseList', label: t('document.rightToWorkInAustralia') },
    { key: 'workerWorkingChildrenDocResponseList', label: t('document.workingWithChildrenCheck') },
    { key: 'workerComprehensiveInsuranceResponseList', label: t('document.comprehensiveCarInsurance') },
    { key: 'workerCarRegistrationResponseList', label: t('document.carRegistration') },
    { key: 'workerFirstAidDocResponseList', label: t('document.firstAid') },
    { key: 'workerCprDocResponseList', label: t('document.cprCertificate') },
    { key: "workerInfectionControlResponseList", label: t("trainingTag.workerInfectionControl") },
    { key: "workerMedicationManagementResponseList", label: t("trainingTag.workerMedicationManagement") },
    { key: 'workerAgreementFileResponseList', label: t('document.employmentAgreement') },
    { key: 'workerAgreementTaxFileResponseList', label: t('document.employmentAgreementTax') },
    { key: 'workerResumeResponseList', label: t('document.resume') }
  ];

  const handleAvatarUpload = async (file) => {
    if (file && staffDetail) {
      setIsUploading(true);
      const contentData = {
        id: staffDetail.id,
        address1: staffDetail.address1 || " ",
      };
      const result = await dispatch(updateAvatar({ file, contentData }));
      if (updateAvatar.fulfilled.match(result)) {
        if (onChange) {
          const freshDetail = await dispatch(fetchWorkerDetail({ workerId: staffDetail.id }));
          if (freshDetail.payload) {
            onChange(freshDetail.payload);
          }
        }
      }
      setIsUploading(false);
    }
  };

  const handleActiveWorker = async () => {
    if (!staffDetail) return;
    setIsSubmitting(true);
    const result = await dispatch(activeWorker({ workerId: staffDetail.id }));
    if (activeWorker.fulfilled.match(result)) {
      setShowConfirmActive(false);
      if (onChange) {
        const freshDetail = await dispatch(fetchWorkerDetail({ workerId: staffDetail.id }));
        if (freshDetail.payload) {
          onChange(freshDetail.payload);
        }
      }
    }
    setIsSubmitting(false);
  };

  const handleDeactivateWorker = async () => {
    if (!staffDetail) return;
    setIsSubmitting(true);
    const result = await dispatch(deactivateWorker({ workerId: staffDetail.id, data: { reason: reason, status: AppConfig.SYSTEM_STATUS.DEACTIVATE } }));
    if (deactivateWorker.fulfilled.match(result)) {
      setShowConfirmDeactivate(false);
      setReason('');
      if (onChange) {
        const freshDetail = await dispatch(fetchWorkerDetail({ workerId: staffDetail.id }));
        if (freshDetail.payload) {
          onChange(freshDetail.payload);
        }
      }
    }
    setIsSubmitting(false);
  };

  const handleTerminateWorker = async () => {
    if (!staffDetail) return;
    setIsSubmitting(true);
    const result = await dispatch(terminateWorker({ workerId: staffDetail.id, data: { terminationReason: terminationReason, reason: terminationReason === 'OTHER' ? reason : '', status: AppConfig.SYSTEM_STATUS.TERMINATE } }));
    if (terminateWorker.fulfilled.match(result)) {
      setShowConfirmTerminate(false);
      setTerminationReason('');
      setReason('');
      if (onChange) {
        const freshDetail = await dispatch(fetchWorkerDetail({ workerId: staffDetail.id }));
        if (freshDetail.payload) {
          onChange(freshDetail.payload);
        }
      }
    }
    setIsSubmitting(false);
  };

  const handleReactivateWorker = async () => {
    if (!staffDetail) return;
    setIsSubmitting(true);
    const result = await dispatch(reactivateWorker({ workerId: staffDetail.id, data: { reason: '', status: AppConfig.SYSTEM_STATUS.ACTIVE } }));
    if (reactivateWorker.fulfilled.match(result)) {
      setShowConfirmReactivate(false);
      if (onChange) {
        const freshDetail = await dispatch(fetchWorkerDetail({ workerId: staffDetail.id }));
        if (freshDetail.payload) {
          onChange(freshDetail.payload);
        }
      }
    }
    setIsSubmitting(false);
  };

  const handleReason = (status) => {
    return (
      <Box>
        <Typography variant="body2" gutterBottom style={{ marginBottom: '1rem' }}>
          {status === AppConfig.SYSTEM_STATUS.DEACTIVATE ? t('text.deactivateWorker') : t('text.terminateWorker')}
        </Typography>
        {status === AppConfig.SYSTEM_STATUS.DEACTIVATE ? (
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            multiline
            rows={4}
            label={t('label.reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        ) : (
          <>
            <AppSelect
              label={t('label.reason')}
              defaultValue={""}
              placeholder={t('Select Reason')}
              options={[
                { value: 'VOLUNTARY_CESSATION', label: t('label.voluntary_cessation', 'Voluntary Cessation') },
                { value: 'ILL_HEALTH', label: t('label.ill_health', 'Ill Health') },
                { value: 'DECEASED', label: t('label.deceased', 'Deceased') },
                { value: 'REDUNDANCY', label: t('label.redundancy', 'Redundancy') },
                { value: 'DISMISSAL', label: t('label.dismissal', 'Dismissal') },
                { value: 'CONTRACT_CESSATION', label: t('label.contract_cessation', 'Contract Cessation') },
                { value: 'TRANSFER', label: t('label.transfer', 'Transfer') },
                { value: 'OTHER', label: t('label.other', 'Other') }
              ]}
              onChange={(selectedOption) => {
                setTerminationReason(selectedOption.value);
                if (selectedOption.value !== 'OTHER') {
                  setReason('');
                }
              }}
              value={[
                { value: 'VOLUNTARY_CESSATION', label: t('label.voluntary_cessation', 'Voluntary Cessation') },
                { value: 'ILL_HEALTH', label: t('label.ill_health', 'Ill Health') },
                { value: 'DECEASED', label: t('label.deceased', 'Deceased') },
                { value: 'REDUNDANCY', label: t('label.redundancy', 'Redundancy') },
                { value: 'DISMISSAL', label: t('label.dismissal', 'Dismissal') },
                { value: 'CONTRACT_CESSATION', label: t('label.contract_cessation', 'Contract Cessation') },
                { value: 'TRANSFER', label: t('label.transfer', 'Transfer') },
                { value: 'OTHER', label: t('label.other', 'Other') }
              ].find(opt => opt.value === terminationReason)}
            />
            {terminationReason === 'OTHER' && (
              <Box mt={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  multiline
                  rows={4}
                  label={t('label.reason', 'Reason')}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    );
  };

  const isDocumentVerified = (docListKey) => {
    let list = staffDetail?.[docListKey] ? staffDetail[docListKey] : [];
    if (list.length === 0) {
      return false;
    }

    const sorted = [...list].sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
    return sorted[0] && sorted[0].status === FILE_STATUS.VERIFIED;
  };

  const hasDocument = (docListKey) => {
    const list = staffDetail ? staffDetail[docListKey] : [];
    return list && list.length > 0;
  };

  // const trainingDocKeysRequired = [
  //   'workerNdisSupportingSafeAndEnjoyableMealsResponseList',
  //   'workerSupportingPeopleToStayInfectionFreeResponseList',
  //   'workerSupportingEffectiveCommunicationResponseList',
  //   'workerHandHygieneResponseList',
  //   'workerSafeFoodHandlingResponseList'
  // ];

  const specificDocKeysRequired = [
    'workerNdisWorkerScreeningCheckResponseList',
    'workerRightToWorkInAustraliaResponseList',
    'workerWorkingChildrenDocResponseList',
    'workerFirstAidDocResponseList',
    'workerCprDocResponseList',
    'workerAgreementFileResponseList',
    'workerAgreementTaxFileResponseList',
    'workerNdisOrientationModuleResponseList',
    'workerInfectionControlResponseList',
    'workerMedicationManagementResponseList'
  ];

  // Check enable active button: all training and specific documents are verified, resume is uploaded, and personal info is complete
  // const allTrainingVerified = trainingDocKeysRequired.every(isDocumentVerified);
  const allSpecificVerified = specificDocKeysRequired.every(isDocumentVerified);
  const hasResume = hasDocument('workerResumeResponseList');

  const hasRequiredPersonalInfo =
    staffDetail?.firstName &&
    staffDetail?.lastName &&
    staffDetail?.email &&
    staffDetail?.phone &&
    staffDetail?.dateOfBirth &&
    staffDetail?.gender &&
    staffDetail?.address1 &&
    staffDetail?.postalCode &&
    staffDetail?.emergencyContactName &&
    staffDetail?.emergencyContactNumber &&
    staffDetail?.workStatus;

  const getActiveStaffTooltip = () => {
    if (!hasRequiredPersonalInfo && !allSpecificVerified && !hasResume) {
      return t("text.activeStaffNotCompletedAll");
    } else if (!hasRequiredPersonalInfo) {
      return t("text.activeStaffNotCompletedInfo");
    } else if (!allSpecificVerified || !hasResume) {
      return t("text.activeStaffNotCompletedDocs");
    }
    return "";
  }

  const canActive = allSpecificVerified && hasResume && hasRequiredPersonalInfo;
  const activeStaffTooltip = getActiveStaffTooltip();

  return (
    <Box className={classes.container}>
      <Box className={classes.avatarWrapper}>
        <Formik
          enableReinitialize
          initialValues={{ avatar: staffDetail?.avatarAws3Link || "" }}
        >
          {() => (
            <Form>
              <Field
                component={AvatarUpload}
                name="avatar"
                id="avatar"
                onUploadFile={handleAvatarUpload}
              />
            </Form>
          )}
        </Formik>
      </Box>
      <Typography className={classes.name}>
        {staffDetail?.firstName} {staffDetail?.lastName}
      </Typography>
      <Typography className={classes.role}>
        {t(`worker_type.${staffDetail?.workerType}`)}
      </Typography>
      {(() => {
        const currentStatus = staffDetail?.approvalStatus === AppConfig.APPROVAL_STATUS.ACTIVE
          ? staffDetail.status
          : staffDetail?.approvalStatus;

        const statusBgColor = AppConfig.SYSTEM_STATUS.ACTIVE === currentStatus
          ? "#CCFBF1"
          : AppConfig.SYSTEM_STATUS.DEACTIVATE === currentStatus || AppConfig.APPROVAL_STATUS.WAITING_FOR_APPROVAL === currentStatus
            ? AppColor.orange
            : AppColor.red;

        const statusTextColor = AppConfig.SYSTEM_STATUS.ACTIVE === currentStatus ? "#0D9488" : AppColor.white;

        const statusTranslationKey = (currentStatus === AppConfig.APPROVAL_STATUS.REJECT || currentStatus === AppConfig.APPROVAL_STATUS.PENDING || currentStatus === AppConfig.APPROVAL_STATUS.WAITING_FOR_APPROVAL)
          ? `approval_status.${currentStatus}`
          : `system_status.${currentStatus}`;

        return (
          <Box
            className={classes.statusPill}
            style={{ backgroundColor: statusBgColor, color: statusTextColor }}
          >
            {t(statusTranslationKey)}
          </Box>
        );
      })()}

      <Box className={classes.onboardingSection}>
        <Typography className={classes.onboardingTitle}>
          {t("label.onboarding")}
        </Typography>
        {onboardingItems.map((docType, index) => {
          const list = staffDetail ? staffDetail[docType.key] : [];
          const sorted = list ? [...list].sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()) : [];
          const item = sorted[0] || null;

          // if (!list || list.length === 0) return null;
          let status = item ? (item.status || FILE_STATUS.PENDING) : FILE_STATUS.NOT_UPLOADED;
          let expiry = item?.expireDate ? moment(item?.expireDate) : null;
          let statusDisplay = null;
          let statusClass = classes.warningPill;
          if (expiry) {
            const now = moment();
            const diffDays = expiry.diff(now, 'days');
            if (diffDays < 0) {
              status = 'OVERDUE';
              statusDisplay = t('fileStatus.OVERDUE');
              statusClass = classes.dangerPill;
            } else if (diffDays <= 90) {
              statusDisplay = `Exp ${expiry.format('MMM YYYY')}`;
              statusClass = classes.warningPill;
            } else {
              statusDisplay = t(`fileStatus.${status}`);
              statusClass =
                (status === FILE_STATUS.VERIFIED)
                  ? classes.donePill
                  : status === FILE_STATUS.PENDING
                    ? classes.warningPill
                    : classes.dangerPill;
            }
          } else {
            statusDisplay = t(`fileStatus.${status}`);
            statusClass =
              (status === FILE_STATUS.VERIFIED)
                ? classes.donePill
                : status === FILE_STATUS.PENDING
                  ? classes.warningPill
                  : classes.dangerPill;
          }
          return (
            <Box key={index} className={classes.onboardingItem}>
              <span className={classes.itemLabel}>{t(docType.label)}</span>
              <span className={statusClass}>
                {statusDisplay}
              </span>
            </Box>
          )
        })}
      </Box>
      {(staffDetail?.approvalStatus === AppConfig.APPROVAL_STATUS.PENDING || staffDetail?.approvalStatus === AppConfig.APPROVAL_STATUS.WAITING_FOR_APPROVAL) && authInfo.role === SYSTEM_ROLE.COMPANY && (
        <>
          <AppButton
            label={t("customUI.resendOnboardingLink") || "Resend onboarding link"}
            onClick={onResendOnboarding}
            style={{ width: "100%", marginTop: 20, fontSize: 11, textTransform: 'none' }}
            buttonColor={AppColor.mainColor}
            mode="outlined"
            startIcon={<i className="fas fa-paper-plane" style={{ fontSize: 14 }}></i>}
          />
          <Tooltip title={activeStaffTooltip} placement="top">
            <span style={{ display: "block" }}>
              <AppButton
                label={t("button.active")}
                onClick={() => setShowConfirmActive(true)}
                style={{ width: "100%", marginTop: 20, fontSize: 11, textTransform: 'none' }}
                buttonColor={AppColor.mainColor}
                isLoading={isSubmitting}
                disabled={isSubmitting || !canActive}
              />
            </span>
          </Tooltip>
          {/* <AppButton
            label={t("button.reject") || "Reject"}
            onClick={() => setShowConfirmReject(true)}
            style={{ width: "100%", marginTop: 10, fontSize: 11, textTransform: 'none' }}
            buttonColor={AppColor.red}
            isLoading={isSubmitting}
            disabled={isSubmitting}
          /> */}
        </>
      )}

      {staffDetail?.approvalStatus === AppConfig.APPROVAL_STATUS.ACTIVE && authInfo.role === SYSTEM_ROLE.COMPANY && (
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
          {
            staffDetail.status === AppConfig.SYSTEM_STATUS.DEACTIVATE &&
            <AppButton
              label={`${t("button.reactivate")}`}
              onClick={() => setShowConfirmReactivate(true)}
              style={{ width: "100%", fontSize: 11, textTransform: 'none' }}
              buttonColor={AppColor.mainColor}
              isLoading={isSubmitting}
              disabled={isSubmitting}
            />
          }
          {
            staffDetail.status === AppConfig.SYSTEM_STATUS.ACTIVE &&
            <AppButton
              label={`${t("button.deactivate")}`}
              onClick={() => setShowConfirmDeactivate(true)}
              style={{ width: "100%", fontSize: 11, textTransform: 'none' }}
              buttonColor={AppColor.orange}
              isLoading={isSubmitting}
              disabled={isSubmitting}
            />
          }
          {
            staffDetail.status !== AppConfig.SYSTEM_STATUS.TERMINATE &&
            <AppButton
              label={`${t("button.terminate")}`}
              onClick={() => setShowConfirmTerminate(true)}
              style={{ width: "100%", fontSize: 11, textTransform: 'none' }}
              buttonColor={AppColor.red}
              isLoading={isSubmitting}
              disabled={isSubmitting}
            />
          }
        </Box>
      )}

      <Backdrop className={classes.backdrop} open={isUploading}>
        <CircularProgress color="inherit" size={50} thickness={4} />
      </Backdrop>

      {/* <AppConfirmDialog
        open={showConfirmReject}
        onConfirm={() => handleRejectWorker()}
        onClose={() => setShowConfirmReject(false)}
        title={t('title.rejectInterest')}
        content={t('text.rejectInterest')}
        minWidth={400}
      /> */}

      <AppConfirmDialog
        open={showConfirmActive}
        onConfirm={() => handleActiveWorker()}
        onClose={() => setShowConfirmActive(false)}
        title={t('title.activeWorker')}
        content={t('text.activeWorker')}
        minWidth={400}
      />

      <AppDialog
        open={showConfirmDeactivate}
        onClose={() => setShowConfirmDeactivate(false)}
        actions={[
          {
            label: t('button.cancel'),
            mode: "outlined",
            onPress: () => setShowConfirmDeactivate(false),
            rounder: true,
            disabled: isSubmitting,
          },
          {
            label: t('button.yes'),
            onPress: () => {
              handleDeactivateWorker();
            },
            rounder: true,
            buttonColor: AppColor.pink,
            isLoading: isSubmitting,
            disabled: isSubmitting,
          }
        ]}
        title={t('title.deactivateWorker')}
      >
        {handleReason(AppConfig.SYSTEM_STATUS.DEACTIVATE)}
      </AppDialog>
      <AppConfirmDialog
        open={showConfirmReactivate}
        onConfirm={() => handleReactivateWorker()}
        onClose={() => setShowConfirmReactivate(false)}
        title={t('title.reactivateWorker')}
        content={t('text.activateWorker')}
      />
      <AppDialog
        open={showConfirmTerminate}
        onClose={() => {
          setShowConfirmTerminate(false);
          setTerminationReason('');
          setReason('');
        }}
        title={t('title.terminateWorker')}
        maxWidth={"sm"}
        actions={[
          {
            label: t('button.cancel'),
            mode: "outlined",
            onPress: () => {
              setShowConfirmTerminate(false);
              setTerminationReason('');
              setReason('');
            },
            rounder: true,
            disabled: isSubmitting,
          },
          {
            label: t('button.yes'),
            onPress: () => {
              handleTerminateWorker();
            },
            rounder: true,
            buttonColor: AppColor.pink,
            isLoading: isSubmitting,
            disabled: isSubmitting || !terminationReason?.trim() || (terminationReason === 'OTHER' && !reason.trim()),
          }
        ]}
      >
        {handleReason(AppConfig.SYSTEM_STATUS.TERMINATE)}
        {!terminationReason?.trim() ? (
          <span style={{ color: 'red', fontSize: 13, marginTop: 8, marginLeft: 4, display: 'block' }}>
            * {t('') || "Reason is required."}
          </span>
        ) : (terminationReason === 'OTHER' && !reason.trim()) ? (
          <span style={{ color: 'red', fontSize: 13, marginTop: 8, marginLeft: 4, display: 'block' }}>
            * {t('') || "Please specify the other reason."}
          </span>
        ) : null}
      </AppDialog>
    </Box>
  );
};

StaffOnboarding.propTypes = {
  staffDetail: PropTypes.object,
  onResendOnboarding: PropTypes.func,
  onChange: PropTypes.func,
};

export default StaffOnboarding;
